import { useState, forwardRef, useEffect, useRef, useCallback, useMemo } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import IconCircleInfo from '@/shared/assets/svg/common/icon-circle-info.svg?react';
import { cn } from '@/shared/shadcn/lib/utils';

const COLORS = {
  base: 'var(--color-gray-400)', // 기본
  focus: 'var(--color-blue-500)', // 포커스
  error: 'var(--color-state-error)', // 에러
};

const INFO_VARIANTS = {
  inactive: { opacity: 0, height: 0 },
  active: { opacity: 1, height: 'auto' },
};

interface FloatingLabelInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  height?: string;
  error?: string;
  id?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  className?: string;
  inputClassName?: string;
  defaultMessage?: string;
  indicator?: React.ReactNode;
  type?: string;
  readOnly?: boolean;
  autoComplete?: string;
}

/**
 * 플로팅 라벨 인풋
 * @description
 * - value 있으면 State 모드, 없으면 RHF 모드
 * - 필수 옵션 (required) 있으면 라벨에 별표(*) 표시
 * @example - RHF 모드
 * <FloatingLabelInput {...register('email', { required: true })} label='email' type='email' required />
 * @example - State 모드
 * <FloatingLabelInput value={value} onChange={(e) => setValue(e.target.value)} label='email' type='email' error={'error message'} required />
 */
const FloatingLabelInput = forwardRef<HTMLInputElement, FloatingLabelInputProps>(
  (
    {
      label = 'Label',
      height = '3.38rem',
      error = null,
      id = null,
      value, // 있으면 controlled
      defaultValue = '', // 없으면 uncontrolled
      onChange,
      onBlur,
      onFocus: onFocusProp,
      className = '',
      inputClassName = '',
      defaultMessage = null,
      indicator = null, // 외부에서 indicator를 받을시
      type = 'text',
      readOnly = false,
      autoComplete = 'off',
      ...rest // name, ref, type 등
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // 내용 여부(라벨 위치조정용)
    const [hasContent, setHasContent] = useState(value !== undefined ? value?.length > 0 : !!defaultValue);

    // 자동완성 감지를 위한 값 체크 함수
    // 주의: 의존성 배열에 value를 포함하지 않음
    // - uncontrolled 모드(value === undefined)에서만 사용되므로 value 변경과 무관
    // - value가 변경되어도 함수 로직은 변하지 않음
    const checkInputValue = useCallback(() => {
      if (value === undefined && ref && typeof ref !== 'function' && ref.current) {
        const newHasContent = ref.current.value.length > 0;
        // 불필요한 리렌더링 방지: 값이 같으면 상태 업데이트 스킵
        setHasContent((prev) => (prev !== newHasContent ? newHasContent : prev));
      }
    }, [ref]);

    // uncontrolled + 초기값 = mount 시 DOM 값 검사
    useEffect(() => {
      checkInputValue();
    }, [checkInputValue]);

    // controlled 모드일 때 - 라벨 위치를 props 변화로 동기화
    useEffect(() => {
      if (value !== undefined) {
        const newHasContent = value?.length > 0;
        // 불필요한 리렌더링 방지: 값이 같으면 상태 업데이트 스킵
        setHasContent((prev) => (prev !== newHasContent ? newHasContent : prev));
      }
    }, [value]);

    // cleanup: 타이머 정리
    useEffect(() => {
      return () => {
        if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current);
      };
    }, []);

    const isActive = isFocused || hasContent;
    const hasError = !!error;
    const isRequired = !!rest.required;

    const borderColor = hasError ? COLORS.error : isFocused ? COLORS.focus : COLORS.base;

    // 라벨 애니메이션 variants 메모이제이션 (성능 최적화)
    const labelVariants = useMemo(
      () => ({
        inactive: { top: '50%', y: '-50%', scale: 1, color: 'var(--color-gray-700)' },
        active: {
          top: '-0.4rem',
          y: '0%',
          scale: 0.8,
          color: hasError ? COLORS.error : isFocused ? COLORS.focus : 'var(--color-gray-700)',
        },
      }),
      [hasError, isFocused]
    );

    /* ★ 별표(*) 애니메이션 variants 메모이제이션 (성능 최적화) */
    const starVariants = useMemo(
      () => ({
        inactive: { opacity: 1, scale: 1 },
        active: { opacity: 0, scale: 0, transition: { duration: 0.18 } },
      }),
      []
    );

    return (
      <div className={cn(readOnly && 'pointer-events-none mt-[0.3rem]', className)}>
        <div className='relative' style={{ height }}>
          <motion.label
            initial={isActive ? false : 'inactive'}
            htmlFor={id ?? label}
            className={cn(
              'bg-basic-white pointer-events-none absolute left-2 rounded-lg px-2 text-base leading-[0.8] text-gray-700',
              !isActive && 'cursor-text'
            )}
            variants={labelVariants}
            animate={isActive ? 'active' : 'inactive'}
            transition={{ type: 'tween', duration: 0.2 }}>
            {label}
            <AnimatePresence>
              {isRequired && (
                <motion.span variants={starVariants} animate={isActive ? 'active' : 'inactive'} className='absolute top-0 right-0'>
                  *
                </motion.span>
              )}
            </AnimatePresence>
          </motion.label>

          <input
            ref={ref}
            id={id ?? label}
            // value 있으면 State 모드, 없으면 RHF 모드
            {...(value !== undefined
              ? { value } // State 모드
              : { defaultValue })} // RHF 모드
            onChange={(e) => {
              if (value === undefined) {
                const newHasContent = e.target.value.length > 0;
                // 불필요한 리렌더링 방지: 값이 같으면 상태 업데이트 스킵
                setHasContent((prev) => (prev !== newHasContent ? newHasContent : prev));
              }
              onChange?.(e);
            }}
            onInput={(e) => {
              // 자동완성 감지를 위한 네이티브 input 이벤트 핸들러
              if (value === undefined) {
                const target = e.target as HTMLInputElement;
                const newHasContent = target.value.length > 0;
                // 불필요한 리렌더링 방지: 값이 같으면 상태 업데이트 스킵
                setHasContent((prev) => (prev !== newHasContent ? newHasContent : prev));
              }
            }}
            onFocus={(e) => {
              if (readOnly) return;
              setIsFocused(true);
              // 포커스 시 즉시 체크 (자동완성된 값이 이미 있을 수 있음)
              checkInputValue();
              // 자동완성이 지연될 수 있으므로 짧은 지연 후 재체크
              if (checkTimeoutRef.current) {
                clearTimeout(checkTimeoutRef.current);
              }
              checkTimeoutRef.current = setTimeout(() => {
                checkInputValue();
              }, 100);
              onFocusProp?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              // 타이머 정리
              if (checkTimeoutRef.current) {
                clearTimeout(checkTimeoutRef.current);
                checkTimeoutRef.current = null;
              }
              // blur 시점에도 최종 체크
              checkInputValue();
              onBlur?.(e);
            }}
            className={cn(
              `bg-basic-white text-basic-black h-full w-full rounded-lg px-4! text-base transition-all outline-none placeholder:text-gray-400`,
              inputClassName
            )}
            style={{ border: `1px solid ${borderColor}` }}
            aria-label={label}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${id}-error` : undefined}
            // autoComplete={type === 'password' ? 'new-password' : 'off'}
            spellCheck={false}
            type={type}
            autoComplete={autoComplete}
            {...rest}
            required={false} // 기본 input 동작 방지
          />
          {indicator}
        </div>

        <div className={cn(defaultMessage !== null || (hasError && 'mt-1'))}>
          <AnimatePresence mode='sync' initial={false}>
            {defaultMessage !== null && !hasError ? (
              <motion.p
                key='defaultMessage'
                variants={INFO_VARIANTS}
                initial='inactive'
                animate='active'
                exit='inactive'
                transition={{ duration: 0.2 }}
                className='ml-2 overflow-hidden text-sm whitespace-pre-wrap text-gray-800'>
                {defaultMessage}
              </motion.p>
            ) : hasError ? (
              <motion.div
                key='error'
                variants={INFO_VARIANTS}
                initial='inactive'
                animate='active'
                exit='inactive'
                transition={{ duration: 0.2 }}
                id={`${id}-error`}
                className='ml-2 flex items-center gap-2 overflow-hidden'>
                <span className='flex-center h-4 w-4 shrink-0'>
                  <IconCircleInfo className='[--icon-bg:var(--color-state-error)] [--icon-fg:var(--color-basic-white)]' />
                </span>
                <span className='text-state-error text-sm whitespace-pre-wrap'>{error}</span>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    );
  }
);

export default FloatingLabelInput;
